import { useForm } from "react-hook-form";
import { Label } from "./ui/label";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ui/use-toast";
import { ClipLoader } from "react-spinners";
import { roles } from "../roles";
import { ScrollArea } from "../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "./ui/command";
import { cn } from "../lib/utils";
import useAuth from "../hooks/useAuth";
import { useEffect, useState } from "react";
import {
	useCheckClientLocations,
	useGetAllClients,
	useGetClientById,
} from "../migration-api/api-hooks/clients";
import { useEditUser } from "../migration-api/api-hooks/users";
import { useGetAllRoles } from "../migration-api/api-hooks/roles";

function EditUser({ user }) {
	const [selectedEmail, setSelectedEmail] = useState("");
	const [selectedClientId, setSelectedClientId] = useState("");
	const [clientLocations, setClientLocations] = useState([]);

	const { toast } = useToast();
	const navigate = useNavigate();
	const { auth } = useAuth();

	// Determine user role
	const isSuperAdmin = auth?.user?.role === "SUPER_ADMIN";
	const isAdmin = auth?.user?.role !== "SUPER_ADMIN";
	const clientId = auth?.user?.clientId;

	const formSchema = z.object({
		email: z.string().email({
			message: "Only valid email is allowed",
		}),
		location: z.string({
			message: "Please select user selection",
		}),
		firstName: z.string({
			message: "This field is required",
		}),
		// clientId: z.number({
		//   message: 'Please select a client'
		// }),
		lastName: z.string({
			message: "This field is required",
		}),
		clientId: z.coerce.number({
			message: "Please select a client",
		}),
		role: z.coerce.number({
			message: "Please select a role",
		}),
	});
	const form = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: {
			firstName: user.firstName || "N/A",
			lastName: user.lastName || "NA",
			email: user.email || "NA",
			location: user.location,
			role: null,
			clientId: user.clientId || null,
		},
	});

	// Fetch all clients
	const { data: clientsData, isLoading: isLoadingClients } =
		useGetAllClients();
	const allClients = clientsData?.data || [];

	// Fetch a client
	const { data: singleClientResponse, isLoading: isLoadingSingleClient } =
		useGetClientById(clientId, {
			enabled: isAdmin && !!clientId,
		});
	const client = singleClientResponse?.data || [];

	// Derived a consistent clients array
	const getClientsForDisplay = () => {
		if (isSuperAdmin) {
			return allClients;
		}
		// For admins, return array with their single client (only if loaded)
		return client && client.id ? [client] : [];
	};

	const clientsForDisplay = getClientsForDisplay();
	const isLoadingClientData = isSuperAdmin
		? isLoadingClients
		: isLoadingSingleClient;

	// Fetch all roles
	const { data: rolesData, isLoading: isLoadingRoles } = useGetAllRoles();
	const allRoles = rolesData?.data || [];

	console.log("allRoles", allRoles);

	// Check/fetch client locations
	const { mutate: checkClientLocations, isLoading: isLoadingLocations } =
		useCheckClientLocations({
			onSuccess: (data) => {
				console.log("Locations loaded:", data);
				const locations = data?.data?.locations || [];
				setClientLocations(locations);
			},
			onError: (error) => {
				console.error("Error loading locations:", error);
				toast({
					variant: "destructive",
					title: "Error",
					description: "Failed to load locations",
				});
				setClientLocations([]);
			},
		});

	const handleClientSelect = (clientId, clientEmail) => {
		console.log("Client selected, ID:", clientId);

		// Update form values
		form.setValue("clientKey", clientId);
		form.setValue("location", ""); // Clear location when client changes

		// Update state
		setSelectedClientId(clientId);
		setSelectedEmail(clientEmail);
		setClientLocations([]); // Clear previous locations

		// Fetch locations for the selected client
		checkClientLocations({ client_email: clientEmail });
	};

	// Get all role values from the roles object
	const roleOptions = Object.values(roles).filter(
		(role) => role !== "all_roles",
	);

	const { editUser, isLoading: isEditing } = useEditUser({
		onSuccess: () => {
			toast({
				variant: "success",
				title: "Success",
				description: `User Updated Succcessfully`,
			});
		},
		onError: (error) => {
			toast({
				variant: "destructive",
				title: "Error",
				description: `${error?.response?.data?.message || "Error editing user"}`,
			});
		},
	});

	// useEffect to auto-select the client for admins
	useEffect(() => {
		if (isAdmin && client && client.id && !form.getValues("clientId")) {
			handleClientSelect(client.id.toString(), client.email);
		}
	}, [client, isAdmin]);

	function onSubmit(data) {
		console.log("Submitting edited data:", data);
		editUser({ ...data, id: user.id });
	}

	return (
		<Form {...form} className="">
			<ScrollArea className="h-[90vh] w-full px-4">
				<div className="mb-[25px] h-fit px-[10px]">
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="space-y-8"
					>
						<fieldset>
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<Label>First Name</Label>
										<FormControl>
											<Input
												type="text"
												placeholder={"first name"}
												autoComplete="off"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<br />
							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<Label>Last Name</Label>
										<FormControl>
											<Input
												type="text"
												placeholder={"last name"}
												autoComplete="off"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<br />
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<Label>Email</Label>
										<FormControl>
											<Input
												type="email"
												placeholder={"johndoe@mail.com"}
												autoComplete="off"
												{...field}
												disabled
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<br />
							{/* <FormField
                control={form.control}
                name='location'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>location</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='User location' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isSuccess &&
                          data?.data?.data?.map((locations, index) => (
                            <SelectItem
                              value={locations.gln}
                              key={index}
                              className='text-12'
                            >
                              {locations.address}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
							<br />
						</fieldset>

						<hr />
						<div className="my-[30px] h-fit px-[10px]">
							<h2 className="mb-2">Edit User Roles</h2>
							<div className="space-y-6">
								{/* Roles to edit */}
								{/* Role Selection - Using Select dropdown */}
								<FormField
									control={form.control}
									name="role"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Select Role</FormLabel>
											<Select
												onValueChange={field.onChange}
												value={field.value}
												disabled={isLoadingRoles}
											>
												<FormControl>
													<SelectTrigger>
														<SelectValue
															placeholder={
																isLoadingRoles
																	? "Loading roles..."
																	: "Select a role"
															}
														/>
													</SelectTrigger>
												</FormControl>
												<SelectContent className="max-h-[300px]">
													{allRoles.map((role) => (
														<SelectItem
															key={role.id}
															value={role.id.toString()}
														>
															{role.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						<hr />

						<hr />
						<div className="mt-[30px] mb-[80px] px-[10px]">
							<h2 className="mb-2">Edit User Clients</h2>
							<div className="flex gap-y-[10px] flex-wrap justify-between">
								<FormField
									control={form.control}
									name="clientKey"
									render={({ field }) => (
										<FormItem className="flex flex-col">
											<FormLabel>Clients</FormLabel>
											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant="outline"
															role="combobox"
															className={cn(
																"w-[100%] justify-between",
																!field.value
																	?.length &&
																	"text-muted-foreground",
															)}
															disabled={
																isLoadingClientData
															}
														>
															{isLoadingClientData
																? "Loading clients..."
																: field.value
																	? clientsForDisplay.find(
																			(
																				c,
																			) =>
																				c.id.toString() ===
																				field.value,
																		)
																			?.clientName
																	: "Select client"}

															{/* {field.value?.length
                              ? `${field.value.length} selected`
                              : 'Select clients'} */}
															<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-[100%] p-0">
													<Command>
														<CommandGroup>
															{clientsForDisplay.map(
																(client) => (
																	<CommandItem
																		value={
																			client.clientName
																		}
																		key={
																			client.id
																		}
																		onSelect={() =>
																			handleClientSelect(
																				client.id.toString(),
																				client.email,
																			)
																		}
																	>
																		<Check
																			className={cn(
																				"mr-2 h-4 w-4",
																				client.id.toString() ===
																					field.value
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																		{
																			client.clientName
																		}
																	</CommandItem>
																),
															)}
														</CommandGroup>
													</Command>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<FormField
							control={form.control}
							name="location"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>Location</FormLabel>
									<Popover>
										<PopoverTrigger asChild>
											<FormControl>
												<Button
													variant="outline"
													role="combobox"
													className={cn(
														"w-[100%] justify-between",
														!field.value &&
															"text-muted-foreground",
													)}
													disabled={
														!selectedClientId ||
														isLoadingLocations
													}
												>
													{isLoadingLocations
														? "Loading locations..."
														: field.value &&
															  clientLocations.length >
																	0
															? clientLocations.find(
																	(
																		location,
																	) =>
																		location?.gln ===
																		field.value,
																)?.address
															: selectedClientId
																? "Select location"
																: "Select a client first"}
													<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
												</Button>
											</FormControl>
										</PopoverTrigger>
										<PopoverContent className="w-[100%] p-0">
											<Command>
												<CommandInput placeholder="Search location.." />
												<CommandEmpty>
													{selectedClientId
														? "No locations found for this client"
														: "Select a client first"}
												</CommandEmpty>
												<CommandGroup>
													{clientLocations.map(
														(location) => (
															<CommandItem
																value={
																	location.address
																}
																key={
																	location.gln
																}
																onSelect={() => {
																	form.setValue(
																		"location",
																		location.gln,
																	);
																}}
															>
																<Check
																	className={cn(
																		"mr-2 h-4 w-4",
																		location.gln ===
																			field.value
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
																{
																	location.address
																}
															</CommandItem>
														),
													)}
												</CommandGroup>
											</Command>
										</PopoverContent>
									</Popover>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button type="submit" className="my-[5px]">
							{isEditing ? (
								<span className="mx-[20px]">
									<ClipLoader color="primary" size={20} />
								</span>
							) : (
								<span>Submit</span>
							)}
						</Button>
					</form>
				</div>
			</ScrollArea>
		</Form>
	);
}

export default EditUser;
